import { useQuery } from '@tanstack/react-query'
import { pb } from '@/lib/pocketbase'
import type { Tender, TenderVersion, VTenderLatestVersion, VTendersPublic } from '@/lib/pocketbase'

// Tender list query options
export interface TenderFilters {
  search?: string
  status?: string[]
  province?: string
  agencyId?: string
  procurementMethod?: string[]
  minBudget?: number
  maxBudget?: number
  dateFrom?: string
  dateTo?: string
  tags?: string[]
  page?: number
  perPage?: number
}

// Fetch tender list from public view
function buildTenderFilter(filters: TenderFilters) {
  const {
    search,
    status,
    province,
    procurementMethod,
    minBudget,
    maxBudget,
    dateFrom,
    dateTo,
  } = filters
  const filterParts: string[] = []
  const budgetFields = ['sum_price_agree', 'procured_amount_satang', 'budget_amount_satang']
  const dateFields = ['published_at']

  if (search) {
    filterParts.push(
      pb.filter(
        '(title_th ~ {:search} || egp_project_no ~ {:search} || agency.name_th ~ {:search} || agency.name_en ~ {:search})',
        {
        search,
        }
      )
    )
  }
  if (status && status.length > 0) {
    const params = Object.fromEntries(status.map((value, index) => [`status${index}`, value]))
    const statusFilter = status.map((_, index) => `project_status = {:status${index}}`).join(' || ')
    filterParts.push(pb.filter(`(${statusFilter})`, params))
  }
  if (province) {
    filterParts.push(pb.filter('tender.province_code = {:province}', { province }))
  }
  if (procurementMethod && procurementMethod.length > 0) {
    const params = Object.fromEntries(procurementMethod.map((value, index) => [`method${index}`, value]))
    const methodFilter = procurementMethod.map((_, index) => `procurement_method = {:method${index}}`).join(' || ')
    filterParts.push(pb.filter(`(${methodFilter})`, params))
  }
  if (typeof minBudget === 'number' && Number.isFinite(minBudget)) {
    const minFilter = budgetFields.map((field) => `${field} >= {:minBudget}`).join(' || ')
    filterParts.push(pb.filter(`(${minFilter})`, { minBudget }))
  }
  if (typeof maxBudget === 'number' && Number.isFinite(maxBudget)) {
    const maxFilter = budgetFields.map((field) => `${field} <= {:maxBudget}`).join(' || ')
    filterParts.push(pb.filter(`(${maxFilter})`, { maxBudget }))
  }
  if (dateFrom) {
    const fromFilter = dateFields.map((field) => `${field} >= {:dateFrom}`).join(' || ')
    filterParts.push(pb.filter(`(${fromFilter})`, { dateFrom }))
  }
  if (dateTo) {
    const toFilter = dateFields.map((field) => `${field} <= {:dateTo}`).join(' || ')
    filterParts.push(pb.filter(`(${toFilter})`, { dateTo }))
  }

  return filterParts.join(' && ')
}

export function useTenderList(filters: TenderFilters = {}) {
  const {
    page = 1,
    perPage = 20,
  } = filters

  return useQuery({
    queryKey: ['tenders', filters],
    queryFn: async () => {
      const filter = buildTenderFilter(filters)
      const result = await pb.collection('v_tenders_public').getList<VTendersPublic>(page, perPage, {
        filter,
        expand: 'agency,tender',
      })

      const mappedItems = result.items.map((item) => ({
        ...item,
        tender_id: (item as any).tender || item.tender_id || item.id,
        project_id: (item as any).project_id ?? (item as any).egp_project_no,
        project_name: (item as any).project_name ?? (item as any).title_th,
        dept_name: (item as any).dept_name ?? (item as any).expand?.agency?.name_th ?? (item as any).expand?.agency?.name_en,
        province_code: (item as any).province_code ?? (item as any).expand?.tender?.province_code,
        sum_price_agree: (item as any).sum_price_agree ?? (item as any).procured_amount_satang ?? (item as any).budget_amount_satang,
      }))

      return { ...result, items: mappedItems }
    },
    staleTime: 30_000, // 30 seconds
  })
}

export function useTenderStats(
  filters: TenderFilters = {},
  options?: { enabled?: boolean }
) {
  const {
    search,
    status,
    procurementMethod,
    province,
    minBudget,
    maxBudget,
    dateFrom,
    dateTo,
    tags,
  } = filters

  return useQuery({
    queryKey: [
      'tender-stats',
      { search, status, procurementMethod, province, minBudget, maxBudget, dateFrom, dateTo, tags },
    ],
    queryFn: async () => {
      const filter = buildTenderFilter(filters)
      const records = await pb.collection('v_tenders_public').getFullList<VTendersPublic>({
        filter,
        batch: 200,
        fields: 'id,project_status,sum_price_agree,procured_amount_satang,budget_amount_satang',
      })

      const totalBudget = records.reduce((sum, item) => {
        const budget =
          (item as any).sum_price_agree ??
          (item as any).procured_amount_satang ??
          (item as any).budget_amount_satang ??
          0
        return sum + budget
      }, 0)

      const biddingCount = records.filter(
        (item) => item.project_status === 'invitation-issued'
      ).length

      return {
        totalCount: records.length,
        totalBudget,
        biddingCount,
      }
    },
    staleTime: 30_000,
    enabled: options?.enabled ?? true,
  })
}

// Fetch single tender with latest version
export function useTender(tenderId: string) {
  return useQuery({
    queryKey: ['tender', tenderId],
    queryFn: async () => {
      const tender = await pb.collection('tenders').getOne<Tender>(tenderId, {
        expand: 'agency,tags',
      })
      return tender
    },
    enabled: !!tenderId,
  })
}

// Fetch tender versions
export function useTenderVersions(tenderId: string) {
  return useQuery({
    queryKey: ['tender-versions', tenderId],
    queryFn: async () => {
      const result = await pb.collection('tender_versions').getList<TenderVersion>(1, 50, {
        filter: `tender = "${tenderId}"`,
        sort: '-version_seq',
      })
      return result.items
    },
    enabled: !!tenderId,
  })
}

// Fetch latest tender version
export function useTenderLatestVersion(tenderId: string) {
  return useQuery({
    queryKey: ['tender-latest-version', tenderId],
    queryFn: async () => {
      const result = await pb.collection('v_tender_latest_versions').getFirstListItem<VTenderLatestVersion>(
        `tender = "${tenderId}"`,
        { expand: 'tender,tender.agency' }
      )

      const tender = (result as any).expand?.tender as Tender | undefined
      const agency = (tender?.expand as any)?.agency

      return {
        ...result,
        tender_id: (result as any).tender || tenderId,
        project_id:
          (result as any).project_id ??
          (result as any).egp_project_no ??
          (tender as any)?.egp_project_no,
        project_name: (tender as any)?.project_name ?? tender?.title_th ?? (result as any).title_th,
        procurement_method: tender?.procurement_method,
        procurement_type: tender?.procurement_type,
        province_code: tender?.province_code,
        dept_name: agency?.name_th ?? agency?.name_en ?? (tender as any)?.agency_name,
        sum_price_agree: (result as any).sum_price_agree ?? (result as any).procured_amount_satang ?? (result as any).budget_amount_satang,
        published_at: (result as any).published_at ?? (result as any).effective_at,
        version_number: (result as any).version_number ?? (result as any).version_seq,
      } as VTenderLatestVersion
    },
    enabled: !!tenderId,
  })
}

// Fetch tender status events (timeline)
export function useTenderTimeline(tenderId: string) {
  return useQuery({
    queryKey: ['tender-timeline', tenderId],
    queryFn: async () => {
      const result = await pb.collection('tender_status_events').getList(1, 100, {
        filter: `tender = "${tenderId}"`,
        sort: '-occurred_at',
      })
      return result.items
    },
    enabled: !!tenderId,
  })
}
